import React, { useRef, useState } from "react";
import { Camera, X } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { securePhoto } from "@/lib/photoSecurity";
import { FindoSpinner } from "@/components/FindoSpinner";

// Reads a File as a base64 data-URL (compressed via canvas to keep payload small).
async function fileToCompressedBase64(file, maxDim = 1280, quality = 0.82) {
  const dataUrl = await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = dataUrl;
  });
  let { width, height } = img;
  if (width > maxDim || height > maxDim) {
    const scale = maxDim / Math.max(width, height);
    width = Math.round(width * scale);
    height = Math.round(height * scale);
  }
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  canvas.getContext("2d").drawImage(img, 0, 0, width, height);
  return canvas.toDataURL("image/jpeg", quality);
}

export default function ImageUpload({ value, onChange, testid = "image-upload", secure }) {
  const ref = useRef(null);
  const { t } = useLang();
  const [securing, setSecuring] = useState(false);

  async function handle(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const b64 = await fileToCompressedBase64(file);
    if (!secure) { onChange(b64); return; }
    setSecuring(true);
    const safe = await securePhoto(b64, secure, t);
    setSecuring(false);
    if (safe) onChange(safe);
  }

  return (
    <div>
      <input ref={ref} type="file" accept="image/*" capture="environment" className="hidden" onChange={handle} data-testid={`${testid}-input`} />
      {securing ? (
        <div className="w-full h-64 rounded-2xl border border-border grid place-items-center bg-slate-50" data-testid={`${testid}-securing`}>
          <span className="flex flex-col items-center gap-2 text-sm text-slate-500"><FindoSpinner size={32} />{t("ph.securing")}</span>
        </div>
      ) : value ? (
        <div className="relative rounded-2xl overflow-hidden border border-border">
          <img src={value} alt={t("hc.item")} className="w-full h-64 object-cover" />
          <button
            type="button"
            onClick={() => onChange("")}
            data-testid={`${testid}-clear`}
            className="absolute top-3 right-3 h-9 w-9 grid place-items-center rounded-full bg-black/60 text-white hover:bg-black/80"
          >
            <X size={18} />
          </button>
          <button
            type="button"
            onClick={() => ref.current?.click()}
            className="absolute bottom-3 right-3 rounded-full bg-white/90 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-white"
          >
            {t("iu.change")}
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => ref.current?.click()}
          data-testid={`${testid}-trigger`}
          className="w-full h-64 rounded-2xl border-2 border-dashed border-border grid place-items-center text-slate-400 hover:border-findo-indigo hover:text-findo-indigo transition-colors bg-slate-50"
        >
          <span className="flex flex-col items-center gap-2">
            <Camera size={36} />
            <span className="text-sm font-semibold">{t("iu.addPhoto")}</span>
          </span>
        </button>
      )}
    </div>
  );
}
